// UserAgreements.jsx

import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Spinner,
  Dropdown,
  Button,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import IconButton from "components/common/IconButton";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import useFormattedDate from "hooks/useFormatDate";
import { Empty, Input, Select } from "antd";
import useDebounce from "hooks/route-rules/useDebounce";

import useUserAgreements from "hooks/useUserAgreements";
// IMPORT THE NEW CUSTOM HOOK
import useAgreementStatusTransitions from "hooks/useAgreementStatusTransitions";

const UserAgreements = () => {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isSearchRequest = debouncedSearchTerm.trim().length >= 3;

  const {
    agreements,
    loading,
    error,
    refetch,
    searchAgreements,
    updateAgreementStatus,
    totalPages,
    totalElements,
  } = useUserAgreements(page, pageSize);

  const { formatDateTime } = useFormattedDate();

  // --- NEW HOOK INTEGRATION ---
  // This hook encapsulates all the complex status change logic
  const {
    showStatusModal,
    setShowStatusModal,
    statusChangeData,
    confirmStatusChange,
    handleStatusChange,
    getStatusModalContent,
    getValidTransitions,
    isEditable,
  } = useAgreementStatusTransitions(updateAgreementStatus, refetch);

  // --- HANDLERS for Navigation & Deletion (Mostly unchanged) ---

  const handleViewAgreement = (id) => {
    navigate(paths.agreementDetails(id));
  };

  const handleEditAgreement = (id) => {
    navigate(paths.agreementEdit(id));
  };

  const handleCreateAgreement = () => {
    navigate(paths.agreementAdd);
  };

  const pillBase = {
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "0.7rem",
    fontWeight: 600,
    lineHeight: 1.2,
    border: "1px solid",
    cursor: "default",
    display: "inline-block",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  };

  const getStatusStyle = (status) => {
    const statusStyles = {
      ACTIVE: { bg: "#e8f7e3", text: "#2e7d32", border: "#d5ebd0" },
      DRAFT: { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db" },
      READY: { bg: "#fef9c3", text: "#a16207", border: "#fde68a" },
      EXPIRED: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
      ABANDONED: { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db" },
      DELETED: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
      DEFAULT: { bg: "#f5f5f5", text: "#616161", border: "#e0e0e0" },
    };

    const style = statusStyles[status] || statusStyles.DEFAULT;
    return {
      ...pillBase,
      backgroundColor: style.bg,
      color: style.text,
      borderColor: style.border,
    };
  };

  const statusFilterOptions = [
    { label: "\u{1F310} ALL", value: "ALL" },
    { label: "\u2705 ACTIVE", value: "ACTIVE" },
    { label: "\u{1F4DD} DRAFT", value: "DRAFT" },
    { label: "\u{1F7E1} READY", value: "READY" },
    { label: "\u{1F534} EXPIRED", value: "EXPIRED" },
    { label: "\u26AA ABANDONED", value: "ABANDONED" },
    { label: "\u26D4 DELETED", value: "DELETED" },
  ];

  const refreshCurrentView = useCallback(() => {
    const trimmed = debouncedSearchTerm.trim();
    const hasStatusFilter = selectedStatus !== "ALL";

    if (trimmed.length === 0 && !hasStatusFilter) {
      refetch();
      return;
    }

    if (trimmed.length > 0 && trimmed.length < 3 && !hasStatusFilter) {
      refetch();
      return;
    }

    searchAgreements(trimmed, selectedStatus, page);
  }, [debouncedSearchTerm, page, refetch, searchAgreements, selectedStatus]);

  useEffect(() => {
    refreshCurrentView();
  }, [refreshCurrentView]);

  // --- COLUMN DEFINITION (useMemo) ---

  const columns = useMemo(
    () => [
      {
        accessorKey: "version",
        header: "Version",
        cell: ({ getValue }) => (
          <span className="text-900" style={{ fontSize: "0.75rem", fontWeight: 600 }}>
            {getValue()}
          </span>
        ),
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "id",
        header: "Agreement ID",
        cell: ({ getValue }) => (
          <span className="text-900 font-monospace" style={{ fontSize: "0.75rem" }}>
            {`${getValue().substring(0, 18)}...`}
          </span>
        ),
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "createdAt",
        header: "Creation Date",
        cell: ({ getValue }) => (
          <span className="text-500 d-inline-block" style={{ fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 }}>
            {formatDateTime(getValue())}
          </span>
        ),
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const value = String(getValue() || "UNKNOWN").toUpperCase();
          const label = value.replaceAll("_", " ");
          return <span style={getStatusStyle(value)}>{label}</span>;
        },
        meta: {
          headerProps: { className: "text-900 text-center" },
          cellProps: { className: "text-center" },
        },
      },
      {
        id: "actions",
        header: "Actions",
        meta: {
          headerProps: { className: "text-900 text-center" },
          cellProps: { className: "text-center" },
        },
        cell: ({ row }) => {
          const item = row.original;
          const currentStatus = item.status;

          const validTransitions = getValidTransitions(currentStatus);

          return (
            <Dropdown className="action-dropdown p-0">
              <Dropdown.Toggle
                as="button"
                className="bg-transparent border-0 p-0 shadow-none text-secondary"
              >
                ⋯
              </Dropdown.Toggle>
              <Dropdown.Menu className="action-menu">
                {/* --- Editing/Viewing --- */}
                {isEditable(currentStatus) && (
                  <Dropdown.Item onClick={() => handleEditAgreement(item.id)}>
                    Edit Content
                  </Dropdown.Item>
                )}
                <Dropdown.Item onClick={() => handleViewAgreement(item.id)}>
                  View Details
                </Dropdown.Item>

                <Dropdown.Divider />

                {/* --- Dynamic Status Transitions --- */}
                {validTransitions.length > 0 && (
                  <>
                    <Dropdown.Header className="pt-0 pb-1 ps-3">
                      Change Status
                    </Dropdown.Header>
                    {validTransitions.map((transition, index) => (
                      <Dropdown.Item
                        key={index}
                        onClick={() =>
                          confirmStatusChange(
                            item.id,
                            transition.targetStatus,
                            currentStatus,
                            item.version,
                          )
                        }
                        className={
                          transition.targetStatus === "ACTIVE"
                            ? "text-success fw-bold"
                            : ""
                        }
                      >
                        {transition.label}
                      </Dropdown.Item>
                    ))}
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          );
        },
      },
    ],
    [
      formatDateTime,
      confirmStatusChange,
      handleEditAgreement,
      handleViewAgreement,
      getValidTransitions,
      isEditable,
      getStatusStyle,
    ],
  );

  const table = useAdvanceTable({
    data: agreements,
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  if (error) {
    return (
      <div className="alert alert-danger d-flex align-items-center justify-content-between">
        <div>Error loading user agreements: {error}</div>
      </div>
    );
  }

  // Pass the stored version from state to the modal content helper
  const modalContent = statusChangeData.version
    ? getStatusModalContent(
        statusChangeData.newStatus,
        statusChangeData.currentStatus,
        statusChangeData.version,
      )
    : {};

  return (
    <>
      <WidgetSectionTitle
        transform="shrink-2"
        className="mb-3 mt-3"
        title="User Agreements"
        icon="handshake"
        subtitle="Manage all versions of Terms of Service, Privacy Policies, and other legal agreements."
      />

      <div className="d-flex justify-content-end mb-3">
        <IconButton
          variant="success"
          size="sm"
          icon="plus"
          transform="shrink-3"
          onClick={handleCreateAgreement}
          className="text-xs fw-bold"
          style={{ color: "#fff" }}
        >
          <span className="ms-1">Add Agreement</span>
        </IconButton>
      </div>

      <AdvanceTableProvider {...table}>
        <Row
          className="mb-3 align-items-end gx-3 p-2 rounded bg-light mx-auto"
          style={{ maxWidth: 980 }}
        >
          <Col md={6}>
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F50E} Search Agreements"}
            </div>
            <Input
              placeholder='Search by "id" or "version" (min. 3 chars)...'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              allowClear
              style={{
                width: "60%",
                borderRadius: 24,
                borderColor:
                  searchTerm.length > 0 && searchTerm.length < 3 ? "#ffa940" : "",
              }}
              suffix={
                loading && isSearchRequest ? (
                  <Spinner animation="border" size="sm" variant="success" />
                ) : (
                  searchTerm.length > 0 &&
                  searchTerm.length < 3 && (
                    <small className="text-warning" style={{ fontSize: "10px" }}>
                      Type {3 - searchTerm.length} more...
                    </small>
                  )
                )
              }
            />
          </Col>
          <Col md={3}>
            <div className="text-muted small fw-semibold mb-1">
              {"\u{1F3F7}\uFE0F Status Filter"}
            </div>
            <Select
              style={{ width: "100%" }}
              value={selectedStatus}
              showSearch
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { minWidth: 220 } } }}
              onChange={(value) => {
                setSelectedStatus(value || "ALL");
                setPage(0);
              }}
              options={statusFilterOptions}
            />
          </Col>
        </Row>

        {loading && agreements.length === 0 ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 200 }}
          >
            <Spinner animation="border" variant="success" />
          </div>
        ) : agreements.length === 0 ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 200 }}
          >
            <Empty
              description={
                isSearchRequest && selectedStatus !== "ALL"
                  ? `No agreements found for "${searchTerm.trim()}" with status "${selectedStatus}".`
                  : isSearchRequest
                  ? `No agreements found for "${searchTerm.trim()}".`
                  : selectedStatus !== "ALL"
                  ? `No agreements found with status "${selectedStatus}".`
                  : "No agreements were found"
              }
            />
          </div>
        ) : (
          <>
            <AdvanceTable
              headerClassName="bg-300 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                bordered: true,
                striped: true,
                className: "fs-10 mb-0 overflow-hidden table-responsive",
                style: { minWidth: "600px" },
              }}
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages || 0}
                totalElements={totalElements || 0}
                onPrev={page > 0 && totalElements > 0 ? () => setPage(page - 1) : undefined}
                onNext={
                  page < totalPages - 1 && totalElements > 0
                    ? () => setPage(page + 1)
                    : undefined
                }
                pageSize={pageSize}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                pageSizeOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </>
        )}
      </AdvanceTableProvider>

      {/* Status Change Modal (Uses logic from the hook) */}
      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalContent.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalContent.body}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button
            variant={modalContent.confirmVariant}
            onClick={() =>
              handleStatusChange(
                statusChangeData.id,
                statusChangeData.newStatus,
              )
            }
          >
            {modalContent.confirmLabel}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserAgreements;
